import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { getUserOrders, getTopUpHistory, updateSettings, updateAvatar, removeAvatar, getApiKey, regenerateApiKey, testApiKey, getNotificationPrefs, updateNotificationPrefs, getTickets, createTicket, getTicketMessages, sendTicketMessage, closeTicket, getStock, getApiTasks } from '../utils/api';

const TIER_COLORS = { bronze: '#cd7f32', silver: '#c0c0c0', gold: '#ffd700' };
const TIER_NEXT = { bronze: { next: 'Silver', amount: 25 }, silver: { next: 'Gold', amount: 100 }, gold: { next: null, amount: null } };
const ACHIEVEMENT_META = {
  first_visit: { label: 'First Visit', icon: '👋', desc: 'Created an account' },
  first_purchase: { label: 'First Purchase', icon: '🎉', desc: 'Completed your first order' },
  ten_orders: { label: '10 Orders', icon: '🔥', desc: 'Placed 10 completed orders' },
  big_spender: { label: 'Big Spender', icon: '💎', desc: 'Spent $100 or more' }
};

// User-facing API doc snippets per language
const API_DOC_SNIPPETS = {
  python: {
    purchase: `import requests\n\nAPI_KEY = "YOUR_API_KEY"\nBASE_URL = "https://fastboost.cc/api/user-api"\n\n# Purchase boosts for a server\nresponse = requests.post(\n    f"{BASE_URL}/purchase",\n    headers={"Authorization": f"Bearer {API_KEY}"},\n    json={\n        "serverInvite": "discord.gg/example",\n        "productId": "1m",       # "1m" or "3m"\n        "quantity": 14,\n        "nickname": "Cool Name",  # optional\n        "bio": "About me text",   # optional\n        "avatar": "https://i.imgur.com/abc.png",  # optional, image URL\n        "banner": "https://i.imgur.com/xyz.png"   # optional, image URL\n    }\n)\nprint(response.json())\n# {"success": true, "orderId": "...", "cost": 4.00,\n#  "remainingBalance": 21.50}`,
    status: `import requests\n\nAPI_KEY = "YOUR_API_KEY"\nBASE_URL = "https://fastboost.cc/api/user-api"\n\n# Check order status\norder_id = "abc123-def456"\nresponse = requests.get(\n    f"{BASE_URL}/order/{order_id}",\n    headers={"Authorization": f"Bearer {API_KEY}"}\n)\nprint(response.json())`,
    balance: `import requests\n\nAPI_KEY = "YOUR_API_KEY"\nBASE_URL = "https://fastboost.cc/api/user-api"\n\n# Check account balance\nresponse = requests.get(\n    f"{BASE_URL}/balance",\n    headers={"Authorization": f"Bearer {API_KEY}"}\n)\nprint(response.json())\n# {"balance": 25.50, "currency": "USD"}`,
    stock: `import requests\n\nBASE_URL = "https://fastboost.cc"\n\n# Get current stock (no API key needed)\n# This is the same endpoint the website\n# polls every 5 seconds\nresponse = requests.get(f"{BASE_URL}/api/get-stock")\nproducts = response.json()\n\nfor p in products:\n    print(p['product'], ':', p['stock'], 'in stock')\n    print('  Price: $' + str(p['pricePerBoost']) + '/boost')\n    print('  Status:', p['status'])\n\n# Response: [\n#   {"id": "boost_1m", "product": "1 Month",\n#    "stock": 500, "status": "In Stock",\n#    "pricePerBoost": 0.40, ...},\n#   {"id": "boost_3m", "product": "3 Months",\n#    "stock": 200, "status": "In Stock",\n#    "pricePerBoost": 0.70, ...}\n# ]`,
    errors: `# Common Error Codes\n\n# 401 - Invalid or missing API key\n# 400 - Missing required fields\n# 402 - Insufficient balance\n# 404 - Order not found\n# 403 - Account banned\n# 429 - Rate limit exceeded\n# 500 - Internal server error\n\n# Error Response Format:\n# {"error": "Description of the error"}\n\n# Purchase Payload Fields:\n#   serverInvite  (required) - Discord invite\n#   productId     (required) - "1m" or "3m"\n#   quantity      (required) - 2-100\n#   nickname      (optional) - Account nickname\n#   bio           (optional) - Account bio\n#   avatar        (optional) - Image URL\n#   banner        (optional) - Image URL`
  },
  nodejs: {
    purchase: `const axios = require('axios');\n\nconst API_KEY = 'YOUR_API_KEY';\nconst BASE_URL = 'https://fastboost.cc/api/user-api';\n\n// Purchase boosts for a server\nconst res = await axios.post(\n  \`\${BASE_URL}/purchase\`,\n  {\n    serverInvite: 'discord.gg/example',\n    productId: '1m',       // '1m' or '3m'\n    quantity: 14,\n    nickname: 'Cool Name', // optional\n    bio: 'About me text',  // optional\n    avatar: 'https://i.imgur.com/abc.png', // optional\n    banner: 'https://i.imgur.com/xyz.png'  // optional\n  },\n  { headers: { Authorization: \`Bearer \${API_KEY}\` } }\n);\nconsole.log(res.data);`,
    status: `const axios = require('axios');\n\nconst API_KEY = 'YOUR_API_KEY';\nconst BASE_URL = 'https://fastboost.cc/api/user-api';\n\n// Check order status\nconst orderId = 'abc123-def456';\nconst res = await axios.get(\n  \`\${BASE_URL}/order/\${orderId}\`,\n  { headers: { Authorization: \`Bearer \${API_KEY}\` } }\n);\nconsole.log(res.data);`,
    balance: `const axios = require('axios');\n\nconst API_KEY = 'YOUR_API_KEY';\nconst BASE_URL = 'https://fastboost.cc/api/user-api';\n\n// Check account balance\nconst res = await axios.get(\n  \`\${BASE_URL}/balance\`,\n  { headers: { Authorization: \`Bearer \${API_KEY}\` } }\n);\nconsole.log(res.data);\n// { balance: 25.50, currency: 'USD' }`,
    stock: `const axios = require('axios');\n\nconst BASE_URL = 'https://fastboost.cc';\n\n// Get current stock (no API key needed)\nconst res = await axios.get(\n  \`\${BASE_URL}/api/get-stock\`\n);\n\nres.data.forEach(p => {\n  console.log(\`\${p.product}: \${p.stock} in stock\`);\n  console.log(\`  Price: $\${p.pricePerBoost}/boost\`);\n});`,
    errors: `// Common Error Codes\n\n// 401 - Invalid or missing API key\n// 400 - Missing required fields\n// 402 - Insufficient balance\n// 404 - Order not found\n// 403 - Account banned\n// 429 - Rate limit exceeded\n// 500 - Internal server error\n\n// Error Response Format:\n// { "error": "Description of the error" }\n\n// Purchase Payload Fields:\n//   serverInvite  (required) - Discord invite\n//   productId     (required) - "1m" or "3m"\n//   quantity      (required) - 2-100\n//   nickname      (optional) - Account nickname\n//   bio           (optional) - Account bio\n//   avatar        (optional) - Image URL\n//   banner        (optional) - Image URL`
  },
  go: {
    purchase: `package main\n\nimport (\n\t"bytes"\n\t"encoding/json"\n\t"fmt"\n\t"net/http"\n)\n\nconst apiKey = "YOUR_API_KEY"\nconst baseURL = "https://fastboost.cc/api/user-api"\n\nfunc main() {\n\tbody, _ := json.Marshal(map[string]interface{}{\n\t\t"serverInvite": "discord.gg/example",\n\t\t"productId":    "1m",\n\t\t"quantity":     14,\n\t\t"nickname":     "Cool Name",\n\t\t"bio":          "About me text",\n\t\t"avatar":       "https://i.imgur.com/abc.png",\n\t\t"banner":       "https://i.imgur.com/xyz.png",\n\t})\n\treq, _ := http.NewRequest("POST",\n\t\tbaseURL+"/purchase",\n\t\tbytes.NewBuffer(body))\n\treq.Header.Set("Authorization", "Bearer "+apiKey)\n\treq.Header.Set("Content-Type", "application/json")\n\tclient := &http.Client{}\n\tresp, _ := client.Do(req)\n\tdefer resp.Body.Close()\n\tfmt.Println(resp.Status)\n}`,
    status: `package main\n\nimport (\n\t"fmt"\n\t"net/http"\n\t"io/ioutil"\n)\n\nconst apiKey = "YOUR_API_KEY"\nconst baseURL = "https://fastboost.cc/api/user-api"\n\nfunc main() {\n\torderID := "abc123-def456"\n\treq, _ := http.NewRequest("GET",\n\t\tbaseURL+"/order/"+orderID, nil)\n\treq.Header.Set("Authorization", "Bearer "+apiKey)\n\tclient := &http.Client{}\n\tresp, _ := client.Do(req)\n\tdefer resp.Body.Close()\n\tbody, _ := ioutil.ReadAll(resp.Body)\n\tfmt.Println(string(body))\n}`,
    balance: `package main\n\nimport (\n\t"fmt"\n\t"net/http"\n\t"io/ioutil"\n)\n\nconst apiKey = "YOUR_API_KEY"\nconst baseURL = "https://fastboost.cc/api/user-api"\n\nfunc main() {\n\treq, _ := http.NewRequest("GET",\n\t\tbaseURL+"/balance", nil)\n\treq.Header.Set("Authorization", "Bearer "+apiKey)\n\tclient := &http.Client{}\n\tresp, _ := client.Do(req)\n\tdefer resp.Body.Close()\n\tbody, _ := ioutil.ReadAll(resp.Body)\n\tfmt.Println(string(body))\n\t// {"balance": 25.50, "currency": "USD"}\n}`,
    stock: `package main\n\nimport (\n\t"fmt"\n\t"net/http"\n\t"io/ioutil"\n)\n\nconst baseURL = "https://fastboost.cc"\n\nfunc main() {\n\treq, _ := http.NewRequest("GET",\n\t\tbaseURL+"/api/get-stock", nil)\n\tclient := &http.Client{}\n\tresp, _ := client.Do(req)\n\tdefer resp.Body.Close()\n\tbody, _ := ioutil.ReadAll(resp.Body)\n\tfmt.Println(string(body))\n}`,
    errors: `// Common Error Codes\n\n// 401 - Invalid or missing API key\n// 400 - Missing required fields\n// 402 - Insufficient balance\n// 404 - Order not found\n// 403 - Account banned\n// 429 - Rate limit exceeded\n// 500 - Internal server error\n\n// Error Response Format:\n// {"error": "Description of the error"}\n\n// Purchase Payload Fields:\n//   serverInvite  (required) - Discord invite\n//   productId     (required) - "1m" or "3m"\n//   quantity      (required) - 2-100\n//   nickname      (optional) - Account nickname\n//   bio           (optional) - Account bio\n//   avatar        (optional) - Image URL\n//   banner        (optional) - Image URL`
  }
};

const LANG_ICONS = {
  python: <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 2C6.48 2 6 4.02 6 5.5V8h6v1H5.5C3.02 9 1 10.52 1 13.5S3.02 18 5.5 18H8v-2.5c0-2.48 2.02-4.5 4.5-4.5h5c2.21 0 4-1.79 4-4V5.5C21.5 3.02 17.52 2 12 2zM9.5 5.5a1 1 0 110 2 1 1 0 010-2z" fill="#3776AB"/><path d="M12 22c5.52 0 6-2.02 6-3.5V16h-6v-1h6.5c2.48 0 4.5-1.52 4.5-4.5S20.98 6 18.5 6H16v2.5c0 2.48-2.02 4.5-4.5 4.5h-5c-2.21 0-4 1.79-4 4v1.5C2.5 20.98 6.48 22 12 22zm2.5-3.5a1 1 0 110-2 1 1 0 010 2z" fill="#FFD43B"/></svg>,
  nodejs: <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><path d="M12 2l9.5 5.5v11L12 24l-9.5-5.5v-11L12 2z" fill="#339933" opacity="0.2"/><path d="M12 2l9.5 5.5v11L12 24l-9.5-5.5v-11L12 2z" stroke="#339933" strokeWidth="1.5" fill="none"/><text x="6" y="16" fill="#339933" fontSize="8" fontWeight="bold">JS</text></svg>,
  go: <svg width="18" height="18" viewBox="0 0 24 24" fill="none"><rect x="2" y="4" width="20" height="16" rx="3" fill="#00ADD8" opacity="0.15"/><text x="5" y="16" fill="#00ADD8" fontSize="9" fontWeight="bold">Go</text></svg>
};

const Dashboard = () => {
  const { user, refreshUser } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'overview');
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [topups, setTopups] = useState([]);
  const [showTopups, setShowTopups] = useState(false);
  const [settingsForm, setSettingsForm] = useState({ username: '', currentPassword: '', newPassword: '' });
  const [settingsMsg, setSettingsMsg] = useState({ type: '', text: '' });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);
  const avatarInputRef = useRef(null);

  // API Key state
  const [apiKey, setApiKey] = useState('');
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [apiKeyCopied, setApiKeyCopied] = useState(false);
  const [apiRefTab, setApiRefTab] = useState('purchase');
  const [apiLang, setApiLang] = useState('python');

  // Notification state
  const [notifPrefs, setNotifPrefs] = useState({ emailNotifs: true, restockAlerts: true, productAlerts: {} });
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifMsg, setNotifMsg] = useState('');
  const [products, setProducts] = useState([]);
  const [notifSaved, setNotifSaved] = useState(false);

  // Support ticket state
  const [tickets, setTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [showNewTicket, setShowNewTicket] = useState(false);
  const [ticketForm, setTicketForm] = useState({ subject: '', category: 'general_support', description: '' });
  const [ticketImage, setTicketImage] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [ticketMessages, setTicketMessages] = useState([]);
  const [ticketMsgInput, setTicketMsgInput] = useState('');
  const [ticketMsgImage, setTicketMsgImage] = useState(null);
  const [ticketSending, setTicketSending] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => { if (!user) navigate('/'); }, [user, navigate]);
  useEffect(() => { if (user) setSettingsForm(f => ({ ...f, username: user.username })); }, [user]);
  useEffect(() => { const tab = searchParams.get('tab'); if (tab) setActiveTab(tab); }, [searchParams]);

  useEffect(() => {
    if (activeTab === 'orders' && user) {
      setOrdersLoading(true);
      Promise.all([
        getUserOrders().then(d => setOrders(d.orders || [])).catch(() => {}),
        getTopUpHistory().then(d => setTopups(d.topups || [])).catch(() => {})
      ]).finally(() => setOrdersLoading(false));
    }
    if (activeTab === 'api' && user) {
      setApiKeyLoading(true);
      getApiKey().then(d => setApiKey(d.apiKey || '')).catch(() => {}).finally(() => setApiKeyLoading(false));
    }
    if (activeTab === 'notifications' && user) {
      setNotifSaved(false);
      getNotificationPrefs().then(d => { if (d) setNotifPrefs(d); }).catch(() => {});
      getStock().then(d => setProducts(d)).catch(() => {});
    }
    if (activeTab === 'support' && user) {
      setTicketsLoading(true);
      getTickets().then(d => setTickets(d.tickets || [])).catch(() => {}).finally(() => setTicketsLoading(false));
    }
  }, [activeTab, user]);

  useEffect(() => {
    if (selectedTicket) {
      getTicketMessages(selectedTicket.id).then(d => setTicketMessages(d.messages || [])).catch(() => {});
    }
  }, [selectedTicket]);
  useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [ticketMessages]);

  const handleSaveSettings = async () => {
    setSettingsLoading(true); setSettingsMsg({ type: '', text: '' });
    try {
      const payload = {};
      if (settingsForm.username !== user.username) payload.username = settingsForm.username;
      if (settingsForm.newPassword) { payload.currentPassword = settingsForm.currentPassword; payload.newPassword = settingsForm.newPassword; }
      if (Object.keys(payload).length === 0) { setSettingsMsg({ type: 'error', text: t('dashboard.noChanges') }); setSettingsLoading(false); return; }
      await updateSettings(payload);
      setSettingsMsg({ type: 'success', text: t('dashboard.settingsUpdated') });
      setSettingsForm(f => ({ ...f, currentPassword: '', newPassword: '' }));
      refreshUser();
    } catch (err) { setSettingsMsg({ type: 'error', text: err.message }); }
    finally { setSettingsLoading(false); }
  };

  const handleAvatarChange = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setAvatarUploading(true);
    try { const formData = new FormData(); formData.append('avatar', file); await updateAvatar(formData); refreshUser(); } catch (err) { alert(err.message); }
    finally { setAvatarUploading(false); }
  };

  const handleRemoveAvatar = async () => {
    try { await removeAvatar(); refreshUser(); } catch (err) { alert(err.message); }
  };

  const handleRegenerateApiKey = async () => {
    if (!window.confirm('Are you sure? This will invalidate the current key.')) return;
    setApiKeyLoading(true);
    try { const d = await regenerateApiKey(); setApiKey(d.apiKey); } catch (err) { alert(err.message); }
    finally { setApiKeyLoading(false); }
  };

  const handleSaveNotifs = async () => {
    setNotifLoading(true);
    try {
      await updateNotificationPrefs(notifPrefs);
      setNotifMsg('Preferences saved!');
      setNotifSaved(true);
      setTimeout(() => setNotifMsg(''), 3000);
    } catch (err) { setNotifMsg(err.message); }
    finally { setNotifLoading(false); }
  };

  const handleCreateTicket = async () => {
    if (!ticketForm.subject || !ticketForm.description) return;
    const formData = new FormData();
    formData.append('subject', ticketForm.subject);
    formData.append('category', ticketForm.category);
    formData.append('description', ticketForm.description);
    if (ticketImage) formData.append('image', ticketImage);
    try {
      await createTicket(formData);
      setShowNewTicket(false);
      setTicketForm({ subject: '', category: 'general_support', description: '' });
      setTicketImage(null);
      const d = await getTickets();
      setTickets(d.tickets || []);
    } catch (err) { alert(err.message); }
  };

  const handleSendTicketMsg = async () => {
    if (!ticketMsgInput.trim() && !ticketMsgImage) return;
    setTicketSending(true);
    const formData = new FormData();
    formData.append('message', ticketMsgInput);
    if (ticketMsgImage) formData.append('image', ticketMsgImage);
    try {
      await sendTicketMessage(selectedTicket.id, formData);
      setTicketMsgInput(''); setTicketMsgImage(null);
      const d = await getTicketMessages(selectedTicket.id);
      setTicketMessages(d.messages || []);
    } catch (err) { alert(err.message); }
    finally { setTicketSending(false); }
  };

  const handleCloseTicket = async () => {
    try {
      await closeTicket(selectedTicket.id);
      setSelectedTicket(null);
      const d = await getTickets();
      setTickets(d.tickets || []);
    } catch (err) { alert(err.message); }
  };

  if (!user) return null;

  const isBanned = user.banned || user.blocked;
  const tierInfo = TIER_NEXT[user.loyaltyTier] || {};
  const progress = tierInfo.amount ? Math.min((user.totalSpent / tierInfo.amount) * 100, 100) : 100;
  const initials = user.username.slice(0, 2).toUpperCase();

  const formatDate = (ts) => new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const statusColor = (s) => ({ completed: '#22c55e', confirmed: '#22c55e', processing: '#f59e0b', waiting: '#6b7280', expired: '#ef4444', failed: '#ef4444', partial: '#f59e0b', pending: '#6b7280' }[s] || '#6b7280');
  const categoryLabels = { general_support: 'General Support', purchase: 'Purchase', other_payment: 'Other Payment', other: 'Other' };

  const handleCopyApiKey = () => { navigator.clipboard.writeText(apiKey); setApiKeyCopied(true); setTimeout(() => setApiKeyCopied(false), 2000); };
  const handleCopyCode = (text) => { navigator.clipboard.writeText(text); };

  const tabList = ['overview', 'orders', 'settings', 'api', 'notifications', 'support'];

  return (
    <main className="main-content">
      <div className="dashboard-page">
        {isBanned && (
          <div className="banned-banner animate-drop">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/></svg>
            <div><strong>Account Banned</strong><p>Your account has been suspended. All purchases and features are disabled.</p></div>
          </div>
        )}

        <div className="dashboard-header animate-drop">
          <div className="dashboard-user-card">
            <div className="dashboard-avatar-wrap">
              <div className="dashboard-avatar" style={{ borderColor: TIER_COLORS[user.loyaltyTier] }}>
                {user.avatar ? <img src={user.avatar} alt="" style={{width:'100%',height:'100%',objectFit:'cover',borderRadius:'50%'}} /> : initials}
              </div>
            </div>
            <div className="dashboard-user-info">
              <div style={{display:'flex',alignItems:'center',gap:'0.75rem',flexWrap:'wrap'}}>
                <h1>{user.username}</h1>
                <Link to="/topup" className="dash-balance-badge" title="Click to top up">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12V7H5a2 2 0 0 1 0-4h14v4"/><path d="M3 5v14a2 2 0 0 0 2 2h16v-5"/><path d="M18 12a2 2 0 1 0 0 4 2 2 0 0 0 0-4z"/></svg>
                  ${(user.balance || 0).toFixed(2)}
                </Link>
              </div>
              <span className="dashboard-tier-badge" style={user.reseller ? { background: 'rgba(168,85,247,0.15)', color: '#a855f7', border: '1px solid rgba(168,85,247,0.3)' } : { background: TIER_COLORS[user.loyaltyTier] + '22', color: TIER_COLORS[user.loyaltyTier], border: `1px solid ${TIER_COLORS[user.loyaltyTier]}44` }}>
                {user.reseller ? 'Official Reseller' : (user.loyaltyTier.charAt(0).toUpperCase() + user.loyaltyTier.slice(1) + ' ' + t('dashboard.member'))}
              </span>
            </div>
          </div>
          <div className="dashboard-stats-row">
            <div className="dash-stat"><span className="dash-stat-value">{user.orderCount}</span><span className="dash-stat-label">{t('dashboard.orders')}</span></div>
            <div className="dash-stat"><span className="dash-stat-value">${user.totalSpent.toFixed(2)}</span><span className="dash-stat-label">Total Spent</span></div>
            <div className="dash-stat"><span className="dash-stat-value">{user.achievements?.length || 0}</span><span className="dash-stat-label">{t('dashboard.achievements')}</span></div>
          </div>
        </div>

        <div className="dashboard-tabs animate-drop delay-1">
          {tabList.map(tb => (
            <button key={tb} className={`dashboard-tab ${activeTab === tb ? 'active' : ''}`} onClick={() => setActiveTab(tb)}>
              {t(`dashboard.${tb === 'api' ? 'apiKeys' : tb === 'support' ? 'supportTab' : tb}`)}
            </button>
          ))}
        </div>

        <div className="dashboard-content animate-drop delay-2">
          {/* OVERVIEW TAB */}
          {activeTab === 'overview' && (
            <div className="dashboard-overview">
              <div className="dash-card">
                <h3>{t('dashboard.loyaltyProgress')}</h3>
                {tierInfo.next ? (
                  <><div className="loyalty-bar-container"><div className="loyalty-bar" style={{ width: `${progress}%`, background: TIER_COLORS[user.loyaltyTier] }} /></div><p className="loyalty-text">${user.totalSpent.toFixed(2)} / ${tierInfo.amount} {t('dashboard.toReach')} {tierInfo.next}</p></>
                ) : (<p className="loyalty-text" style={{ color: '#ffd700' }}>{t('dashboard.highestTier')}</p>)}
              </div>
              <div className="dash-card">
                <h3>{t('dashboard.achievements')}</h3>
                <div className="achievements-grid">
                  {Object.entries(ACHIEVEMENT_META).map(([key, meta]) => {
                    const unlocked = user.achievements?.includes(key);
                    return (<div key={key} className={`achievement-item ${unlocked ? 'unlocked' : 'locked'}`}><span className="achievement-icon">{meta.icon}</span><div><div className="achievement-label">{meta.label}</div><div className="achievement-desc">{meta.desc}</div></div>{unlocked && <span className="achievement-check">✓</span>}</div>);
                  })}
                </div>
              </div>
            </div>
          )}

          {/* ORDERS TAB */}
          {activeTab === 'orders' && (
            <div className="dash-card">
              <h3>{t('dashboard.orderHistory')}</h3>
              {ordersLoading ? (<div style={{ textAlign: 'center', padding: '2rem' }}><span className="loading-spinner" /></div>) : orders.length === 0 && topups.length === 0 ? (<p style={{ color: 'var(--text-muted)', textAlign: 'center', padding: '2rem' }}>{t('dashboard.noOrders')}</p>) : (
                <>
                  {orders.length > 0 && (
                    <div className="orders-table">
                      {orders.map(o => (
                        <div key={o.id} className="order-row" onClick={() => window.open(`/checkout/${o.id}`, '_blank')}>
                          <div className="order-row-main">
                            <div>
                            <div className="order-row-product">
                                {o.billingType === 'annual' ? (
                                  <>
                                    {o.quantity}x Boosts — Annual ~{' '}
                                    {(o.annualMonths || 12) === 12
                                      ? '1 Year'
                                      : `${o.annualMonths} Months`}
                                    <span className="order-annual-badge">
                                      Annual ({o.annualMonths || 12}mo)
                                    </span>
                                  </>
                                ) : (
                                  <>
                                    {o.quantity}x Boosts — {o.productName}
                                  </>
                                )}
                              </div>
                              <div className="order-row-date">{formatDate(o.createdAt)}</div>
                            </div>
                            <div className="order-row-right">
                              <span className="order-row-total">${o.total?.toFixed(2)}</span>
                              <span className="order-row-method">{o.paymentMethod === 'stripe' ? 'Card' : o.cryptoType}</span>
                              <span style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', gap: 3 }}>
                                Powered by {o.paymentMethod === 'stripe' ? 'Stripe' : 'Nowpayments.io'}
                              </span>
                              <span className="order-row-status" style={{ color: statusColor(o.boostStatus || o.status) }}>
                                {o.boostStatus === 'completed' ? 'Completed' : o.boostStatus === 'processing' ? 'Processing' : o.status === 'expired' ? 'Expired' : o.status === 'confirmed' ? 'Confirmed' : o.status === 'waiting' ? 'Pending' : o.status}
                              </span>
                            </div>
                          </div>
                          {/* Show annual reboost info if applicable */}
                          {o.billingType === 'annual' && o.nextReboostDate && (
                            <div className="order-annual-info">
                              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                              Next reboost: {new Date(o.nextReboostDate).toLocaleDateString()} · Expires: {o.annualEndDate ? new Date(o.annualEndDate).toLocaleDateString() : '—'}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Top-Up History */}
                  {topups.length > 0 && (
                    <>
                      <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginTop: orders.length > 0 ? '1.5rem' : 0, marginBottom:'0.75rem'}}>
                        <h4 style={{margin:0,fontSize:'0.95rem',color:'var(--text-primary)'}}>Top-Up History</h4>
                        <button onClick={() => setShowTopups(!showTopups)} style={{background:'none',border:'1px solid var(--border-color)',borderRadius:8,padding:'4px 12px',fontSize:'0.8rem',color:'var(--text-secondary)',cursor:'pointer'}}>
                          {showTopups ? 'Hide' : `Show (${topups.length})`}
                        </button>
                      </div>
                      {showTopups && (
                        <div className="orders-table">
                          {topups.map(t => (
                            <div key={t.id} className="order-row" style={{cursor:'default'}}>
                              <div className="order-row-main">
                                <div>
                                  <div className="order-row-product" style={{display:'flex',alignItems:'center',gap:'0.5rem'}}>
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2"><path d="M12 2v20m-7-7l7 7 7-7"/></svg>
                                    Top Up +${t.amount?.toFixed(2)}
                                  </div>
                                  <div className="order-row-date">{formatDate(t.createdAt)}</div>
                                </div>
                                <div className="order-row-right">
                                  <span className="order-row-total" style={{color:'#22c55e'}}>+${t.amount?.toFixed(2)}</span>
                                  <span className="order-row-method">{t.paymentMethod === 'stripe' ? 'Card' : t.cryptoType}</span>
                                  <span className="order-row-status" style={{color: t.status === 'confirmed' ? '#22c55e' : t.status === 'waiting' ? '#f59e0b' : '#9ca3af'}}>
                                    {t.status === 'confirmed' ? 'Confirmed' : t.status === 'waiting' ? 'Pending' : t.status}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          )}

          {/* SETTINGS TAB */}
          {activeTab === 'settings' && (
            <div className="dash-card">
              <h3>{t('dashboard.accountSettings')}</h3>
              {settingsMsg.text && <div className={`auth-error ${settingsMsg.type === 'success' ? 'success' : ''}`}>{settingsMsg.text}</div>}
              <div className="settings-form">
                <div className="form-group">
                  <label className="form-label">{t('dashboard.profileAvatar')}</label>
                  <div className="avatar-upload-row">
                    <div className="avatar-preview" style={{ borderColor: TIER_COLORS[user.loyaltyTier] }}>
                      {user.avatar ? <img src={user.avatar} alt="" /> : <span>{initials}</span>}
                    </div>
                    <div className="avatar-actions">
                      <button className="btn btn-secondary avatar-btn" onClick={() => avatarInputRef.current?.click()} disabled={avatarUploading}>{avatarUploading ? <span className="loading-spinner"/> : t('dashboard.changeAvatar')}</button>
                      {user.avatar && <button className="btn btn-secondary avatar-btn" onClick={handleRemoveAvatar}>{t('dashboard.removeAvatar')}</button>}
                      <input ref={avatarInputRef} type="file" accept="image/*" onChange={handleAvatarChange} style={{display:'none'}} />
                    </div>
                  </div>
                </div>
                <div className="form-group">
                  <label className="form-label">{t('auth.email')}</label>
                  <input type="email" className="form-input" value={user.email} disabled style={{ opacity: 0.5 }} />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('auth.username')}</label>
                  <input type="text" className="form-input" value={settingsForm.username} onChange={e => setSettingsForm({ ...settingsForm, username: e.target.value })} maxLength={20} />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('dashboard.currentPassword')}</label>
                  <input type="password" className="form-input" placeholder="Required to change password" value={settingsForm.currentPassword} onChange={e => setSettingsForm({ ...settingsForm, currentPassword: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">{t('dashboard.newPassword')}</label>
                  <input type="password" className="form-input" placeholder="Leave blank to keep current" value={settingsForm.newPassword} onChange={e => setSettingsForm({ ...settingsForm, newPassword: e.target.value })} />
                </div>
                <button className="btn btn-primary" onClick={handleSaveSettings} disabled={settingsLoading}>{settingsLoading ? <span className="loading-spinner" /> : t('dashboard.saveChanges')}</button>
              </div>
            </div>
          )}

          {/* API KEYS TAB - User-facing API docs */}
          {activeTab === 'api' && (
            <div className="api-tab-layout">
              <div className="api-top-row">
                <div className="dash-card api-key-card">
                  <div className="api-card-header">
                    <div className="api-card-icon"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="var(--pink-primary, #e8a4b4)" strokeWidth="2"><path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/></svg></div>
                    <h3 style={{margin:0}}>API Key</h3>
                  </div>
                  {apiKeyLoading ? <div style={{textAlign:'center',padding:'1rem'}}><span className="loading-spinner"/></div> : (
                    <>
                      <div className="api-key-meta">
                        <span className="api-active-badge">Active</span>
                        <span className="api-created-date">Created {user?.createdAt ? new Date(user.createdAt).toLocaleDateString('en-CA') : '—'}</span>
                      </div>
                      <div className="api-key-display-v2">
                        <code>{apiKey}</code>
                        <button className="btn-icon" onClick={handleCopyApiKey} title="Copy">
                          {apiKeyCopied ? <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#22c55e" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg> : <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>}
                        </button>
                      </div>
                      <button className="btn btn-regen" onClick={handleRegenerateApiKey}>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="23 4 23 10 17 10"/><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/></svg>
                        Regenerate Key
                      </button>
                      <p className="api-regen-warn">Regenerating will invalidate your current key immediately</p>
                    </>
                  )}
                </div>

                <div className="dash-card api-ref-card">
                  <div className="api-card-header">
                    <div className="api-card-icon ref"><svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#818cf8" strokeWidth="2"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg></div>
                    <div style={{flex:1}}>
                      <h3 style={{margin:0}}>API Reference</h3>
                      <span style={{fontSize:'0.75rem',color:'var(--text-muted)'}}>User API integration guide</span>
                    </div>
                  </div>

                  {/* Language selector */}
                  <div className="api-lang-selector">
                    {['python', 'nodejs', 'go'].map(lang => (
                      <button key={lang} className={`api-lang-btn ${apiLang === lang ? 'active' : ''}`} onClick={() => setApiLang(lang)}>
                        {LANG_ICONS[lang]}
                        <span>{lang === 'nodejs' ? 'Node.js' : lang.charAt(0).toUpperCase() + lang.slice(1)}</span>
                      </button>
                    ))}
                  </div>

                  {/* Endpoint tabs */}
                  <div className="api-ref-tabs">
                    <button className={`api-ref-tab ${apiRefTab === 'purchase' ? 'active' : ''}`} onClick={() => setApiRefTab('purchase')}>
                      <span>+</span> Purchase
                    </button>
                    <button className={`api-ref-tab ${apiRefTab === 'status' ? 'active' : ''}`} onClick={() => setApiRefTab('status')}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg> Status
                    </button>
                    <button className={`api-ref-tab ${apiRefTab === 'balance' ? 'active' : ''}`} onClick={() => setApiRefTab('balance')}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg> Balance
                    </button>
                    <button className={`api-ref-tab ${apiRefTab === 'stock' ? 'active' : ''}`} onClick={() => setApiRefTab('stock')}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg> Stock
                    </button>
                    <button className={`api-ref-tab ${apiRefTab === 'errors' ? 'active' : ''}`} onClick={() => setApiRefTab('errors')}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg> Errors
                    </button>
                  </div>
                  <div className="api-code-block">
                    <div className="api-code-header">
                      <div className="api-code-dots"><span className="dot red"/><span className="dot yellow"/><span className="dot green"/></div>
                      <span className="api-code-title">{apiRefTab === 'purchase' ? 'POST /api/user-api/purchase' : apiRefTab === 'status' ? 'GET /api/user-api/order/:id' : apiRefTab === 'balance' ? 'GET /api/user-api/balance' : apiRefTab === 'stock' ? 'GET /api/get-stock' : 'Error Codes'}</span>
                      <button className="api-copy-btn" onClick={() => handleCopyCode(API_DOC_SNIPPETS[apiLang]?.[apiRefTab] || '')}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg> Copy
                      </button>
                    </div>
                    <pre className="api-code-content">{API_DOC_SNIPPETS[apiLang]?.[apiRefTab] || ''}</pre>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* NOTIFICATIONS TAB */}
          {activeTab === 'notifications' && (
            <div className="dash-card">
              <h3>{t('dashboard.notificationsTitle')}</h3>
              {notifMsg && <div className={`auth-error ${notifSaved ? 'success' : ''}`}>{notifMsg}</div>}
              <div className="notif-section">
                <div className="notif-toggle-row">
                  <div><strong>{t('dashboard.emailNotifications')}</strong></div>
                  <label className="toggle-switch"><input type="checkbox" checked={!!notifPrefs.emailNotifs} onChange={e => setNotifPrefs({...notifPrefs, emailNotifs: e.target.checked})} /><span className="toggle-slider"/></label>
                </div>
                <div className="notif-toggle-row">
                  <div><strong>{t('dashboard.restockAlerts')}</strong><p style={{color:'var(--text-muted)',fontSize:'0.8rem',margin:0}}>{t('dashboard.restockDesc')}</p></div>
                  <label className="toggle-switch"><input type="checkbox" checked={!!notifPrefs.restockAlerts} onChange={e => setNotifPrefs({...notifPrefs, restockAlerts: e.target.checked})} /><span className="toggle-slider"/></label>
                </div>
                {products.length > 0 && (
                  <div className="notif-products-section">
                    <strong>{t('dashboard.productAlerts')}</strong>
                    <p style={{color:'var(--text-muted)',fontSize:'0.8rem'}}>{t('dashboard.productAlertsDesc')}</p>
                    {products.map(p => (
                      <div key={p.id} className="notif-toggle-row">
                        <span>{p.product}</span>
                        <label className="toggle-switch"><input type="checkbox" checked={notifPrefs.productAlerts?.[p.id] !== false} onChange={e => setNotifPrefs({...notifPrefs, productAlerts: {...notifPrefs.productAlerts, [p.id]: e.target.checked}})} /><span className="toggle-slider"/></label>
                      </div>
                    ))}
                  </div>
                )}
                <button className="btn btn-primary" style={{marginTop:'1rem'}} onClick={handleSaveNotifs} disabled={notifLoading}>{notifLoading ? <span className="loading-spinner"/> : t('dashboard.saveNotifications')}</button>
              </div>
            </div>
          )}

          {/* SUPPORT TAB */}
          {activeTab === 'support' && (
            <div className="dash-card">
              {selectedTicket ? (
                <div className="ticket-chat">
                  <div className="ticket-chat-header">
                    <button className="btn-icon" onClick={() => setSelectedTicket(null)}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="19" y1="12" x2="5" y2="12"/><polyline points="12 19 5 12 12 5"/></svg></button>
                    <div><h4>{selectedTicket.subject}</h4><span className={`ticket-status-badge ${selectedTicket.status}`}>{selectedTicket.status}</span></div>
                    {selectedTicket.status === 'open' && <button className="btn btn-secondary" style={{marginLeft:'auto',width:'auto',padding:'0.4rem 0.8rem',fontSize:'0.8rem'}} onClick={handleCloseTicket}>{t('support.closeTicket')}</button>}
                  </div>
                  <div className="ticket-messages">
                    {ticketMessages.map((msg, i) => (
                      <div key={i} className={`ticket-msg ${msg.sender === 'user' ? 'msg-user' : 'msg-admin'}`}>
                        <div className="msg-bubble">
                          {msg.message && <p>{msg.message}</p>}
                          {msg.image && <img src={msg.image} alt="" className="msg-image" />}
                        </div>
                        <span className="msg-time">{new Date(msg.createdAt).toLocaleTimeString()}</span>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>
                  {selectedTicket.status === 'open' && (
                    <div className="ticket-input-row">
                      <button className="btn-icon" onClick={() => document.getElementById('ticket-msg-img')?.click()}><svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg></button>
                      <input id="ticket-msg-img" type="file" accept="image/*" style={{display:'none'}} onChange={e => setTicketMsgImage(e.target.files?.[0] || null)} />
                      <input type="text" className="form-input" placeholder={t('support.typeMessage')} value={ticketMsgInput} onChange={e => setTicketMsgInput(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleSendTicketMsg()} />
                      {ticketMsgImage && <span className="msg-img-badge">📎 {ticketMsgImage.name}</span>}
                      <button className="btn btn-primary" style={{width:'auto',padding:'0.6rem'}} onClick={handleSendTicketMsg} disabled={ticketSending}>{ticketSending ? <span className="loading-spinner"/> : <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>}</button>
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <div className="ticket-header-row">
                    <h3>{t('support.title')}</h3>
                    <button className="btn btn-primary" style={{width:'auto',padding:'0.5rem 1rem'}} onClick={() => setShowNewTicket(true)}>{t('support.newTicket')}</button>
                  </div>
                  {showNewTicket && (
                    <div className="new-ticket-form animate-drop">
                      <div className="form-group"><label className="form-label">{t('support.subject')}</label><input type="text" className="form-input" value={ticketForm.subject} onChange={e => setTicketForm({...ticketForm, subject: e.target.value})} /></div>
                      <div className="form-group"><label className="form-label">Category</label>
                        <select className="form-input" value={ticketForm.category} onChange={e => setTicketForm({...ticketForm, category: e.target.value})}>
                          <option value="general_support">General Support</option><option value="purchase">Purchase</option><option value="other_payment">Other Payment Method</option><option value="other">Other</option>
                        </select>
                      </div>
                      <div className="form-group"><label className="form-label">{t('support.description')}</label><textarea className="form-input" rows={4} value={ticketForm.description} onChange={e => setTicketForm({...ticketForm, description: e.target.value})} /></div>
                      <div className="form-group">
                        <button className="btn btn-secondary" style={{width:'auto'}} onClick={() => document.getElementById('ticket-img')?.click()}>
                          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg> {t('support.attachImage')}
                        </button>
                        <input id="ticket-img" type="file" accept="image/*" style={{display:'none'}} onChange={e => setTicketImage(e.target.files?.[0] || null)} />
                        {ticketImage && <span style={{fontSize:'0.8rem',color:'var(--text-muted)',marginLeft:'0.5rem'}}>📎 {ticketImage.name}</span>}
                      </div>
                      <div style={{display:'flex',gap:'0.5rem'}}>
                        <button className="btn btn-primary" style={{width:'auto'}} onClick={handleCreateTicket}>{t('support.submit')}</button>
                        <button className="btn btn-secondary" style={{width:'auto'}} onClick={() => setShowNewTicket(false)}>{t('checkout.cancel')}</button>
                      </div>
                    </div>
                  )}
                  {ticketsLoading ? <div style={{textAlign:'center',padding:'2rem'}}><span className="loading-spinner"/></div> : tickets.length === 0 ? (
                    <p style={{color:'var(--text-muted)',textAlign:'center',padding:'2rem'}}>{t('support.noTickets')}</p>
                  ) : (
                    <div className="tickets-list">
                      {tickets.map(tk => (
                        <div key={tk.id} className="ticket-row" onClick={() => setSelectedTicket(tk)}>
                          <div><strong>{tk.subject}</strong><span className="ticket-date">{formatDate(tk.createdAt)}</span></div>
                          <div className="ticket-row-right"><span className={`ticket-status-badge ${tk.status}`}>{tk.status === 'open' ? t('support.open') : t('support.closed')}</span><span className="ticket-category-badge">{categoryLabels[tk.category] || tk.category || 'General Support'}</span></div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </main>
  );
};

export default Dashboard;